import {
  Color,
  MaterialParameters,
  MeshBasicMaterial,
  MeshNormalMaterial,
  MeshPhongMaterial,
  Vector2,
  Web3DMeshMaterial,
} from '@technology/web3d-plugin-threejs';
import { MaterialType } from './types/material-type';
import { Web3DMeshMaterialParameters } from '@technology/web3d/dist/esnext/Rendering/Web3DMeshMaterial';
import { GlobalMaterialUniforms } from '@technology/web3d/dist/esnext/Rendering/RenderingManager';

export function getTileMinMax(
  tileIndex: Vector2,
  zoomLevel: number,
  bounds: Vector2[]
): { min: Vector2; max: Vector2 } {
  // The number of tiles along each axis at the current zoom level.
  // This is calculated as 2^z.
  const numTiles = 1 << zoomLevel;

  // The full width and height of the world in projected coordinates.
  const worldSize = bounds[1].clone().sub(bounds[0]);

  // The size of a single tile in projected coordinates.
  const tileSize = worldSize.clone().divideScalar(numTiles);

  // Calculate the min corner (bottom-left) of the tile.
  const min = tileIndex
    .clone()
    .multiply(tileSize) // Position relative to map origin
    .add(bounds[0]); // Add map origin offset

  // The max corner is simply the min corner plus the size of one tile.
  const max = min.clone().add(tileSize);

  return { min, max };
}

export function getMeshMaterial(
  params: {
    appearance: MaterialType;
    opacity?: number;
  },
  uniforms: GlobalMaterialUniforms
):
  | MeshPhongMaterial
  | MeshNormalMaterial
  | Web3DMeshMaterial
  | MeshBasicMaterial {
  let MaterialConstructor;
  const opacity = params.opacity ?? 1;
  let materialParameters: Partial<MaterialParameters> &
    Partial<{ color: Color | number | string }> = {
    polygonOffset: true,
    polygonOffsetFactor: -1.0,
    polygonOffsetUnits: -1.0,
    transparent: opacity < 1,
    opacity,
  };

  switch (params.appearance) {
    case 'basic': {
      MaterialConstructor = MeshBasicMaterial;
      materialParameters = {
        ...materialParameters,
        visible: true,
      };
      break;
    }
    case 'web3d': {
      MaterialConstructor = Web3DMeshMaterial;
      const web3dMaterialParameters = {
        visible: true,
        phong: {
          ambient: 0.65,
          intensity: 0.35,
          specular: 0.2,
          specularHardness: 8,
        },
      };
      materialParameters = {
        ...materialParameters,
        ...web3dMaterialParameters,
      };
      break;
    }
    case 'normal': {
      MaterialConstructor = MeshNormalMaterial;
      break;
    }
    case 'phong': {
      MaterialConstructor = MeshPhongMaterial;

      break;
    }
  }

  return new MaterialConstructor(
    materialParameters as Web3DMeshMaterialParameters,
    uniforms
  );
}

/*
 * This method modifies the material's shader to implement a map mode.
 * In map mode, all vertices are set to a height of zero, effectively flattening the terrain.
 * The shader also makes use of a progress timer to smoothly transition between terrain and map modes.
 *
 */
export function addDisplayModeShader(material: Web3DMeshMaterial): void {
  material.onBeforeCompile = (shader) => {
    const commonInsert = `
        #include <common>
        varying float vHeight;
        uniform int displayMode; // 0 = terrain, 1 = map
        uniform float progress;
        uniform float baseHeight;
    `;

    const vertexInsert = `
        #include <begin_vertex>
        vHeight = transformed.z;

        if(displayMode == 1) {
          if(abs(transformed.z - baseHeight) < 0.1) {
            transformed.z = baseHeight;
          } else {
            transformed.z = baseHeight + (transformed.z - baseHeight) * (1.0-progress);
          }
          transformedNormal = vec3(0.0,0.0,1.0);
        } else {
          if(abs(transformed.z - baseHeight) < 0.1) {
            transformed.z = baseHeight;
          } else {
            transformed.z = baseHeight + (transformed.z - baseHeight) * progress;
          }
        }
    `;

    // Perform the string replacements on the shader code
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      commonInsert
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      vertexInsert
    );

    // Keeping this here for future reference if we want to do fragment shader effects
    // shader.fragmentShader = shader.fragmentShader.replace(
    //   '#include <common>',
    //   commonInsert
    // );
    // shader.fragmentShader = shader.fragmentShader.replace(
    //   'void main() {',
    //   fragmentInsert
    // );

    // console.log(shader.vertexShader);
    // console.log(shader.fragmentShader);
  };
}
